/* import __COLOCATED_TEMPLATE__ from './dac-svg.hbs'; */
import PrettyNum from '@ebiz/pretty-num';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { modifier } from 'ember-modifier';
import intersection from 'lodash.intersection';

import ENV from '../config/environment';
import Constants from '../utils/constants';
import { getAlterations, makeSvgStylesUnique, getSignalChainComponent } from '../utils/svg-utils';
import Utils from '../utils/utils';

export default class DacSvg extends Component {
  @service intl;
  @service adiStyleService;

  hideCustomLabels = false;

  insertSvg = modifier((element) => {
    if (this.args.customLabelMap === null) return;

    const customLabelMap = this.args.customLabelMap;
    const svg = `svgs/topology/${this.getFileName()}.svg`;
    const url = ENV.rootURL[ENV.rootURL.length - 1] === '/' ? `${ENV.rootURL}${svg}` : `${ENV.rootURL}/${svg}`;

    console.time('SVG load');
    fetch(url)
      .then((response) => {
        return response.text();
      })
      .then((svg) => {
        element.innerHTML = svg;
        this.processSvgAlterations(customLabelMap);
        console.timeEnd('SVG load');
      })
      .catch((error) => {
        console.error('Error loading SVG', error);
      });
  });

  getFileName() {
    const selectedDacType = this.args.dacHandler.type,
      selectedDac = this.args.dacHandler.selected,
      fileName = this.args.option.topologySvgName || this.args.option.fileName;

    if (
      selectedDacType &&
      selectedDac &&
      selectedDacType.id === this.args.option.id &&
      (selectedDacType.overrideSvgForDacs || []).includes(selectedDac.name)
    ) {
      return `${fileName}_${selectedDac.name}`;
    } else return fileName;
  }

  processSvgAlterations(customLabelMap) {
    const componentElement = document.querySelector(`#${this.args.id}`);
    if (!componentElement) return;

    const hideElement = (element) => {
      if (element && element.classList) element.classList.add(Constants.CLASSES.HIDDEN_ITEM);
    };

    const resizeViewbox = (svgElement, customViewBox, addTo) => {
      if (!customViewBox) return;

      let viewBox =
        svgElement && svgElement.attributes && svgElement.attributes.viewBox ? svgElement.attributes.viewBox.value : '';
      const viewBoxArray = viewBox.split(' ');

      if (viewBoxArray.length >= 3) {
        let svgMinX = viewBoxArray[0],
          svgMinY = viewBoxArray[1],
          svgWidth = viewBoxArray[2],
          svgHeight = viewBoxArray[3];

        if (customViewBox.minX) svgMinX = addTo ? customViewBox.minX + Number(svgMinX) : customViewBox.minX;
        if (customViewBox.minY) svgMinY = addTo ? customViewBox.minY + Number(svgMinY) : customViewBox.minY;
        if (customViewBox.width) svgWidth = addTo ? customViewBox.width + Number(svgWidth) : customViewBox.width;
        if (customViewBox.height) svgHeight = addTo ? customViewBox.height + Number(svgHeight) : customViewBox.height;

        svgElement.attributes.viewBox.value = `${svgMinX} ${svgMinY} ${svgWidth} ${svgHeight}`;
      }
    };

    const changeTransform = (alterationElement, changeItems) => {
      let tranformAttribute =
        alterationElement && alterationElement.attributes && alterationElement.attributes.transform
          ? alterationElement.attributes.transform.value
          : '';
      if (tranformAttribute.length > 0) {
        alterationElement.attributes.transform.value = changeItems.transform;
      }
    };

    const isSignalChainLinkAlteration = (alteration) => {
      const signalChainItems = Object.values(Constants.SIGNAL_CHAIN_ITEMS);
      return 'linkTypes' in alteration && intersection(signalChainItems, alteration.linkTypes).length > 0;
    };

    const svgContainerElement = componentElement,
      svgElement = svgContainerElement.querySelector('svg');

    let addedOutputVoltageValues = false,
      alterations = getAlterations(
        this.args.option,
        this.args.configTypes,
        this.hideCustomLabels,
        this.args.hideSignalChainLabels,
        isSignalChainLinkAlteration,
      );

    alterations.forEach((alteration) => {
      let alterationElement = alteration.id ? svgContainerElement.querySelector(`#${alteration.id}`) : null;

      if (alteration.type === Constants.SVG.ALTERATION_TYPES.HIDE) hideElement(alterationElement);
      if (alteration.type === Constants.SVG.ALTERATION_TYPES.ADD_LINK) {
        if (customLabelMap && alteration.linkTypes && alteration.linkTypes.length) {
          // Only use the first one found
          const linkType = alteration.linkTypes.find((linkType) => linkType in customLabelMap);

          if (linkType) {
            const currentLinkType = customLabelMap[linkType];

            if (alterationElement) {
              const innerHTML = alterationElement.innerHTML,
                outerHTML = alterationElement.outerHTML.replace(innerHTML, currentLinkType.name),
                result = alterationElement.parentElement.innerHTML.replace(
                  alterationElement.outerHTML,
                  `<a href="${currentLinkType.productPage}" target="_blank">${outerHTML}</a>`,
                );
              alterationElement.parentElement.innerHTML = result;
            }
          }
          // hide the text if we are not making it into a link
          else hideElement(alterationElement);
        }
        // hide the text if we are not making it into a link
        else hideElement(alterationElement);
      } else if (alteration.type === Constants.SVG.ALTERATION_TYPES.ADD_OUTPUT_VOLTAGE) {
        // hide the label if we are not converting it to the output voltage value

        if (this.args.showOutputVoltageValues && alterationElement) {
          const outputVoltageValue = alteration.id in customLabelMap ? customLabelMap[alteration.id] : null,
            voltsLabel = this.intl.t('labels.units.v');
          if (Utils.isNumeric(outputVoltageValue)) {
            alterationElement.innerHTML = alterationElement.innerHTML.replace(
              'X',
              `${PrettyNum.eng4(outputVoltageValue)}${voltsLabel}`,
            );
            addedOutputVoltageValues = true;
          } else hideElement(alterationElement);
        } else if (alterationElement) hideElement(alterationElement);
      } else if (alteration.type === Constants.SVG.ALTERATION_TYPES.RESIZE_VIEWBOX) {
        resizeViewbox(svgElement, alteration);
      } else if (alteration.type === Constants.SVG.ALTERATION_TYPES.CHANGE_TRANSFORM) {
        changeTransform(alterationElement, alteration);
      }
    });

    // Ensure that svg css rules are unique for each svg (with shared classes)
    makeSvgStylesUnique(svgContainerElement);

    // Increase the width of the svg to account for any output voltage values added on the side.
    if (addedOutputVoltageValues) resizeViewbox(svgElement, { width: 20 }, true);

    /**
     * Set a min height to the container (equivalent to the height of the svg)
     * to avoid flickering.
     **/
    // if(svgContainerElement.offsetHeight) {
    //     let selector = `#${svgContainerElement.id}`,
    //         propertyName = 'min-height',
    //         propertyValue = `${svgContainerElement.offsetHeight}px !important`,
    //         minHeight = component.adiStyleService.getValueForSelectorProperty(selector, propertyName);

    //     if(!minHeight) {
    //         let properties = {};
    //         properties[propertyName] = propertyValue;

    //         component.adiStyleService.insertRule({
    //             selector: selector,
    //             properties: properties
    //         });
    //     }
    // }

    const errorDetails = document.querySelector('.error-details-table');
    if (errorDetails) {
      const componentName = document.querySelector('.chart-container svg .selected');
      if (componentName) {
        this.addComponentHighlight(componentName.classList[0]);
      }
    }
  }

  addComponentHighlight(targetName) {
    const component = getSignalChainComponent(targetName);
    if (component) {
      component.classList.add('bold');
    }
  }
}
